<template>
  <div class="meta-block">
    <div class="support-head">
      <div class="support-thumb">
        <img
          v-if="support.thumbnailUrl && !thumbnailFailed"
          :src="support.thumbnailUrl"
          :alt="support.thumbnailAlt || `${support.label} thumbnail`"
          @error="thumbnailFailed = true"
        />
        <span v-else aria-hidden="true">{{ initials }}</span>
      </div>
      <div class="support-copy">
        <h3>{{ support.label }}</h3>
        <div class="sub">{{ support.name }}</div>
      </div>
    </div>
    <div class="chip-row">
      <StatusChip
        v-for="(chip, i) in chips"
        :key="i"
        :label="chip.label"
        :variant="chip.variant"
      />
    </div>
  </div>
</template>

<script>
/**
 * Left-panel topmost block: ring + support title + chips.
 *
 * Prop-driven leaf — data comes from Vuex getters at the panel level
 * (`GET_SUPPORT_HEADER`, `GET_CHIPS`).
 *
 * Styles mirror the Claude Design wireframe `.support-head`, `.meta-block`,
 * and chip-row inline style (AVA Verification Wireframe.html lines 98-113,
 * 586-596).
 */
import StatusChip from '@/features/Verification/components/shared/StatusChip.vue';

export default {
  name: 'SupportHeader',
  components: { StatusChip },
  data() {
    return {
      thumbnailFailed: false,
    };
  },
  props: {
    support: {
      type: Object,
      required: true,
      // Expected shape: { id, label, name, thumbnailUrl, thumbnailAlt }
    },
    chips: {
      type: Array,
      default: () => [],
      // Expected item shape: { label, variant }
    },
  },
  computed: {
    initials() {
      const parts = String(this.support?.label || 'Tag').trim().split(/\s+/);
      return parts.slice(0, 2).map((part) => part.charAt(0)).join('').toUpperCase();
    },
  },
  watch: {
    'support.thumbnailUrl'() {
      this.thumbnailFailed = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/verification-tokens';

.meta-block {
  padding: 14px 0;
  border-bottom: 1px dashed $vf-line-soft;
}

.support-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;

  .support-thumb {
    width: 44px;
    height: 44px;
    flex: 0 0 44px;
    border-radius: $vf-r-md;
    background: #eef5f4;
    border: 1px solid $vf-line;
    display: grid;
    place-items: center;
    overflow: hidden;
    color: $vf-brand-castleton;
    font-family: $vf-font-heading;
    font-weight: 700;
    font-size: 12px;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  }

  h3 {
    font-size: 15px;
    font-weight: 600;
    margin: 0;
  }

  .sub {
    font-size: 11px;
    color: $vf-ink-2;
    margin-top: 2px;
    font-family: $vf-font-mono;
  }
}

.support-copy {
  min-width: 0;

  h3,
  .sub {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.chip-row {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
</style>
